import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet } from "lucide-react";
import SEO from "@/components/SEO";
import { enableLocalMode } from "@/lib/localStore";

const AUTH_BG = "https://images.unsplash.com/photo-1747673002516-f11a48cb0ce2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRleHR1cmUlMjBlYXJ0aCUyMHRvbmVzfGVufDB8fHx8MTc4MzExMDgzNXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, localMode, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && !localMode) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const m = res.migration || {};
      if (m.migrated && m.imported) {
        const n = (m.imported.transactions || 0) + (m.imported.recurring || 0) + (m.imported.category_budgets || 0);
        if (n > 0) toast.success(`Welcome back! We moved ${n} items from your local ledger into your account.`);
      }
      navigate("/dashboard");
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-brand-bg font-body">
      <SEO
        title="Sign in"
        description="Sign in to your Tracker Daily Money ledger. Track money in, money out, and your running balance — quietly and privately."
        path="/login"
        noindex
      />
      <div className="flex flex-col justify-between p-8 md:p-16">
        <Link to="/" className="flex items-center gap-2 w-fit" data-testid="brand-logo">
          <div className="h-9 w-9 rounded-md bg-brand-primary flex items-center justify-center">
            <Wallet className="h-4 w-4 text-brand-bg" strokeWidth={1.75} />
          </div>
          <span className="font-display font-black text-xl tracking-tight text-brand-charcoal">Tracker Daily</span>
        </Link>

        <div className="max-w-md w-full mx-auto md:mx-0 animate-fade-up">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">Welcome back</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-brand-charcoal mb-3">
            Sign in to your ledger
          </h1>
          <p className="text-brand-muted mb-8 leading-relaxed">
            Track every dollar in and every dollar out. Your calm cash flow companion.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-brand-charcoal">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email"
                className="mt-1 border-brand-border bg-brand-surface"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-brand-charcoal">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                className="mt-1 border-brand-border bg-brand-surface"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-brand-primary text-brand-bg hover:bg-brand-primaryHover rounded-md py-6 text-base font-semibold"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-brand-muted">
            New to Tracker Daily?{" "}
            <Link to="/register" className="text-brand-primary font-semibold hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </p>
          <p className="mt-2 text-sm text-brand-muted">
            <Link to="/forgot-password" className="text-brand-primary font-semibold hover:underline" data-testid="link-forgot">
              Forgot your password?
            </Link>
          </p>
          <div className="mt-8 pt-6 border-t border-brand-border">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-muted mb-2">Or</div>
            <Link to="/dashboard" data-testid="login-guest-link" onClick={enableLocalMode}>
              <Button variant="outline" className="border-brand-border w-full py-6 text-base font-semibold">
                Continue without account
              </Button>
            </Link>
            <div className="mt-2 text-xs text-brand-muted">Your data stays on this device. Sign in any time to sync.</div>
          </div>
        </div>

        <div className="text-xs text-brand-muted mt-8">© Tracker Daily Money · Your money, your rhythm</div>
      </div>

      <div className="hidden md:block relative overflow-hidden">
        <img src={AUTH_BG} alt="Abstract earth tones" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary/40 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-brand-bg">
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-2 opacity-90">Cash flow, redefined</div>
          <div className="font-display text-2xl font-bold max-w-md leading-tight">
            &ldquo;A quiet ledger, an honest look at what came in and what went out.&rdquo;
          </div>
        </div>
      </div>
    </div>
  );
}
